import { Component, Input, OnInit } from "@angular/core";
import { FormControl, UntypedFormControl, UntypedFormGroup, ValidatorFn } from "@angular/forms";
import { AuthenticationMethod, Platform } from "src/app/models/api";
import { IntegrationCredentials } from "src/app/models/api/integration.model";
import { CredentialFields, CredentialKey, MultilineCredentialsKeys, SecretCredentialsKeys } from "src/app/models/constants/credentials.constants";
import { PlatformAuthDetailsDescription } from "src/app/models/constants/platform.constants";
import { IDetailKeyValue } from "src/app/models/singlePageComponents";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { LoggerService } from "src/app/services/infra/logger/logger.service";
import { MsalClientService } from "src/app/services/infra/oauthAuthorizationCodeClientService/msalClientService/msal-client-service.service";
import {
    CertificatePrivateKeyCredentialValidatorsFunctions,
    CertificateThumbprintCredentialValidatorsFunctions,
    ChatGptEnterpriseApiKeyCredentialValidatorsFunctions,
    CredentialValidatorsFunctions,
    EmailCredentialValidatorsFunctions,
    GuidCredentialValidatorsFunctions,
    RequiredValidatorFunction,
    SalesforceInstanceUrlCredentialValidatorsFunctions,
    serviceNowInstanceUrlCredentialValidatorsFunctions,
} from "./consts";
import { ServiceNowClientService } from "src/app/services/infra/oauthAuthorizationCodeClientService/serviceNowClientService/serviceNow-client-service.service";

@Component({
    selector: "app-integration-credentials",
    templateUrl: "./integration-credentials.component.html",
    styleUrls: ["./integration-credentials.component.scss"],
})
export class IntegrationCredentialsComponent implements OnInit {
    @Input() public platformControl: FormControl<string>;
    @Input() public authMethodControl: FormControl<string>;
    @Input() public credentialsFormGroup: UntypedFormGroup;
    @Input() public integrationCredentials: IntegrationCredentials;
    @Input() public isInAddMode: boolean;
    @Input() public formSubmitAttempt: boolean;
    @Input() public shouldUpdateCredentials: boolean;

    public svgSignInPath: string;
    public userTryingToSignInUsingOauth = false;
    public userSignedInUsingOauth = false;
    public credentialsDetails: IDetailKeyValue[];
    public formValid = false;

    public AuthenticationMethods = AuthenticationMethod;
    public MOCK_SECRET_TEXT = "●●●●●●●●●●●●●●";

    public authMethodHadChanged = false;

    constructor(
        private _msalClientService: MsalClientService,
        private _labelsProviderPipe: LabelsProviderPipe,
        private _logger: LoggerService,
        private _serviceNowClientService: ServiceNowClientService,
    ) {}

    ngOnInit() {
        this.authMethodControl.valueChanges.subscribe((authMethod) => {
            this.authMethodHadChanged = true;
            this.userTryingToSignInUsingOauth = false;
            this.userSignedInUsingOauth = false;

            if (authMethod) {
                this._populateCredentialsFieldsFromPlatformAndAuthMethod(this.platformControl.value as Platform, authMethod as AuthenticationMethod);
            }

            this._setSvgIcon();
        });

        if (this.authMethodControl.value) {
            this._populateCredentialsFieldsFromPlatformAndAuthMethod(
                this.platformControl.value as Platform,
                this.authMethodControl.value as AuthenticationMethod,
            );
            this._setSvgIcon();
        }
    }

    public signInUsingOauth() {
        this.userTryingToSignInUsingOauth = true;
        switch (this.platformControl.value as Platform) {
            case Platform.powerplatform:
                this._msalClientService.popUpMicrosoftLoginToRetrieveAuthCode().subscribe({
                    next: (authCode: string) => {
                        // if in add mode control already exists
                        if (!this.isInAddMode) {
                            this._addCredentialFormControl(Platform.powerplatform, CredentialKey.authCode);
                            this.credentialsFormGroup.removeControl(CredentialKey.username);
                            this._initDetails();
                        }
                        this.credentialsFormGroup.get(CredentialKey.authCode)?.setValue(authCode);
                        this.userSignedInUsingOauth = true;
                        this.userTryingToSignInUsingOauth = false;
                    },
                    error: (error) => {
                        this._logger.error("Failed to login into Microsoft using OAuth flow, error from Microsoft: " + error);
                        this.userTryingToSignInUsingOauth = false;
                    },
                });
                break;
            case Platform.serviceNow:
                this._serviceNowClientService
                    .popUpServiceNowToRetrieveAuthCode(
                        this.credentialsFormGroup.controls[CredentialKey.serviceNowInstanceUrl].value,
                        this.credentialsFormGroup.controls[CredentialKey.clientId].value,
                    )
                    .subscribe({
                        next: (authCode: string) => {
                            this.credentialsFormGroup.get(CredentialKey.authCode)?.setValue(authCode);
                            this.userSignedInUsingOauth = true;
                            this.userTryingToSignInUsingOauth = false;
                        },
                        error: (error) => {
                            this._logger.error("Failed to login into ServiceNow using OAuth flow, error from ServiceNow: " + error);
                            this.userTryingToSignInUsingOauth = false;
                        },
                    });
                break;
        }
    }

    private _addCredentialFormControl(platform: Platform, credentialKey: CredentialKey, includeValidators = true) {
        let validators: ValidatorFn[] = [];
        if (includeValidators) {
            validators = CredentialValidatorsFunctions;
            switch (credentialKey) {
                case CredentialKey.clientId:
                    if (platform !== Platform.salesforce && platform !== Platform.serviceNow) {
                        validators = GuidCredentialValidatorsFunctions;
                    }
                    break;
                case CredentialKey.tenantId:
                    validators = GuidCredentialValidatorsFunctions;
                    break;
                case CredentialKey.apiKey:
                    if (platform === Platform.chatGptEnterprise) {
                        validators = ChatGptEnterpriseApiKeyCredentialValidatorsFunctions;
                    } else {
                        validators = GuidCredentialValidatorsFunctions;
                    }
                    break;
                case CredentialKey.email:
                    validators = EmailCredentialValidatorsFunctions;
                    break;
                case CredentialKey.username:
                    validators = EmailCredentialValidatorsFunctions;
                    break;
                case CredentialKey.certificateThumbprint:
                    validators = CertificateThumbprintCredentialValidatorsFunctions;
                    break;
                case CredentialKey.certificatePrivateKey:
                    validators = CertificatePrivateKeyCredentialValidatorsFunctions;
                    break;
                case CredentialKey.salesforceInstanceUrl:
                    validators = SalesforceInstanceUrlCredentialValidatorsFunctions;
                    break;
                case CredentialKey.serviceNowInstanceUrl:
                    validators = serviceNowInstanceUrlCredentialValidatorsFunctions;
                    break;
                case CredentialKey.workspaceId:
                    validators = GuidCredentialValidatorsFunctions;
                    break;
            }

            if (this.isInAddMode || !SecretCredentialsKeys.includes(credentialKey)) {
                validators = RequiredValidatorFunction.concat(validators);
            }
        }
        let formControl = new FormControl<string>("", validators);
        if (!this.authMethodHadChanged && this.integrationCredentials && this.integrationCredentials[credentialKey]) {
            formControl = new FormControl<string>(this.integrationCredentials[credentialKey], validators);
        }
        this.credentialsFormGroup.addControl(credentialKey, formControl);
    }

    private _setSvgIcon() {
        switch (this.platformControl.value as Platform) {
            case Platform.powerplatform:
                this.svgSignInPath = "assets/images/common/ms-sign-in.svg";
                break;
            case Platform.serviceNow:
                this.svgSignInPath = "assets/images/common/snow-sign-in.svg";
                break;
        }
    }

    private _initDetails() {
        this.credentialsDetails = [];
        for (const credentialsKey in this.credentialsFormGroup.controls) {
            if (credentialsKey === CredentialKey.authCode) {
                continue;
            }

            const value = this.credentialsFormGroup.get(credentialsKey) as UntypedFormControl;
            this.credentialsDetails.push({
                key: this._labelsProviderPipe.transform(CredentialFields[credentialsKey]),
                formValue: value,
                isCopyable: true,
                isSensitive: SecretCredentialsKeys.includes(<CredentialKey>credentialsKey),
                isMultiline: MultilineCredentialsKeys.includes(<CredentialKey>credentialsKey),
                description: this._getCredentialFieldDescription(
                    this.platformControl.value as Platform,
                    this.authMethodControl.value as AuthenticationMethod,
                    credentialsKey,
                ),
            });
        }
    }

    private _getCredentialFieldDescription(platform: Platform, method: AuthenticationMethod, credentialKey: string) {
        try {
            return this._labelsProviderPipe.transform(PlatformAuthDetailsDescription[platform][method][credentialKey]);
        } catch {
            return "";
        }
    }

    private _populateCredentialsFieldsFromPlatformAndAuthMethod(platform: Platform, authMethod: AuthenticationMethod) {
        this.credentialsFormGroup.controls = {};
        this.credentialsFormGroup.reset();
        switch (platform) {
            case Platform.workato:
                switch (authMethod) {
                    case AuthenticationMethod.apikey:
                        this._addCredentialFormControl(platform, CredentialKey.email);
                        this._addCredentialFormControl(platform, CredentialKey.apiKey);
                        break;
                    case AuthenticationMethod.userpassword:
                        this._addCredentialFormControl(platform, CredentialKey.username);
                        this._addCredentialFormControl(platform, CredentialKey.password);
                        break;
                    case AuthenticationMethod.oktasso:
                        this._addCredentialFormControl(platform, CredentialKey.username);
                        this._addCredentialFormControl(platform, CredentialKey.password);
                        this._addCredentialFormControl(platform, CredentialKey.oktaAppEmbeddedUrl);
                        this._addCredentialFormControl(platform, CredentialKey.oktaUsername);
                        this._addCredentialFormControl(platform, CredentialKey.oktaPassword);
                        break;
                    case AuthenticationMethod.apitoken:
                        this._addCredentialFormControl(platform, CredentialKey.apiToken);
                }
                break;
            case Platform.powerplatform:
                switch (authMethod) {
                    case AuthenticationMethod.userpassword:
                        this._addCredentialFormControl(platform, CredentialKey.tenantId);
                        this._addCredentialFormControl(platform, CredentialKey.clientId);
                        this._addCredentialFormControl(platform, CredentialKey.username);
                        this._addCredentialFormControl(platform, CredentialKey.password);
                        break;
                    case AuthenticationMethod.oauthAuthorizationCode:
                        if (this.isInAddMode || this.authMethodHadChanged) {
                            this._addCredentialFormControl(platform, CredentialKey.authCode);
                        } else {
                            // if editing credentials - we want to show oauth username
                            this._addCredentialFormControl(platform, CredentialKey.username, false);
                        }
                        break;
                    case AuthenticationMethod.serviceprincipal:
                        this._addCredentialFormControl(platform, CredentialKey.tenantId);
                        this._addCredentialFormControl(platform, CredentialKey.clientId);
                        this._addCredentialFormControl(platform, CredentialKey.servicePrincipalSecret);
                        break;
                    case AuthenticationMethod.ppZenityMultiTenantAppServicePrincipal:
                        this._addCredentialFormControl(platform, CredentialKey.tenantId);
                        this._addCredentialFormControl(platform, CredentialKey.servicePrincipalObjectId);
                        break;
                }
                break;
            case Platform.powerBI:
                switch (authMethod) {
                    case AuthenticationMethod.ppZenityMultiTenantAppServicePrincipal:
                        this._addCredentialFormControl(platform, CredentialKey.tenantId);
                        this._addCredentialFormControl(platform, CredentialKey.servicePrincipalObjectId);
                        break;
                }
                break;
            case Platform.salesforce:
                this._addCredentialFormControl(platform, CredentialKey.salesforceInstanceUrl);
                this._addCredentialFormControl(platform, CredentialKey.clientId);
                this._addCredentialFormControl(platform, CredentialKey.clientSecret);
                break;
            case Platform.serviceNow:
                this._addCredentialFormControl(platform, CredentialKey.serviceNowInstanceUrl);
                this._addCredentialFormControl(platform, CredentialKey.clientId);
                this._addCredentialFormControl(platform, CredentialKey.clientSecret);
                this._addCredentialFormControl(platform, CredentialKey.authCode);
                break;
            case Platform.chatGptEnterprise:
                this._addCredentialFormControl(platform, CredentialKey.workspaceId);
                this._addCredentialFormControl(platform, CredentialKey.apiKey);
                break;
        }

        this._initDetails();
    }
}
